import { memo, ReactNode, useCallback, useState } from 'react'
import { Layout, Responsive, WidthProvider } from 'react-grid-layout'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import { LinkButton } from '@components/shared/Button'
import dynamic from 'next/dynamic'
import Orderbook from '@components/orderbook/Orderbook'
import TokenChangeStats from './TokenChangeStats'
import TradesHoldersTabs from './TradesHoldersTabs'
import UserTabs from './UserTabs'
import TokenDetails from './TokenDetails'
import TokenDistribution from './TokenDistribution'
import {
  BREAKPOINTS,
  COLS,
  LayoutComponent,
  Layouts,
  LAYOUTS_STORAGE_KEY,
  makeDefaultLayout,
} from 'utils/grid-layouts'
import {
  ArrowPathRoundedSquareIcon,
  ArrowsPointingOutIcon,
  TrashIcon,
  LockClosedIcon,
  LockOpenIcon,
} from '@heroicons/react/20/solid'
import TradeTabs from '@components/trade/TradeTabs'
import { useTranslation } from 'next-i18next'

const TradingViewChart = dynamic(
  () => import('@components/trade/TradingViewChart'),
  {
    ssr: false,
  },
)

// Initialize the WidthProvider once
const ResponsiveGridLayout = WidthProvider(Responsive)

const TokenGrid = () => {
  const { t } = useTranslation('token-page')
  const [storedLayouts, storeLayouts] = useLocalStorage(LAYOUTS_STORAGE_KEY)
  const [editableLayouts, setEditableLayouts] = useState(() =>
    structuredClone(storedLayouts),
  )
  const [uiLocked, setUiLocked] = useState(true)

  const unlockUiChanges = useCallback(() => {
    setUiLocked(false)
  }, [])

  const storeUiLayoutChanges = useCallback(() => {
    setUiLocked(true)
    storeLayouts(structuredClone(editableLayouts))
  }, [storeLayouts, editableLayouts])

  const discardUiLayoutChanges = useCallback(() => {
    setUiLocked(true)
    setEditableLayouts(structuredClone(storedLayouts))
  }, [storedLayouts])

  const handleLayoutChange = useCallback(
    (_currentLayout: Layout[], changedLayouts: Layouts) => {
      setEditableLayouts(changedLayouts)
    },
    [],
  )

  const resetLayout = useCallback(() => {
    setEditableLayouts(makeDefaultLayout())
  }, [])

  const gridComponents = {
    tradingview: (
      <div className="shared-container h-full p-4">
        <TradingViewChart />
      </div>
    ),
    orderbook: (
      <div className="shared-container h-full overflow-hidden px-4">
        <Orderbook />
      </div>
    ),
    trade: (
      <div className="shared-container h-full">
        <TradeTabs />
      </div>
    ),
    'token-change-stats': <TokenChangeStats />,
    'trades-holders': <TradesHoldersTabs />,
    'user-tabs': <UserTabs />,
    'token-details': <TokenDetails />,
    'token-distribution': <TokenDistribution />,
  } satisfies Record<LayoutComponent, ReactNode>

  return (
    <div>
      <div className="mb-4 flex items-center justify-end gap-4">
        {uiLocked ? (
          <LinkButton onClick={unlockUiChanges}>
            <span className="mr-1 text-xs">{t('grid.customize')}</span>
            <LockClosedIcon className="size-4" />
          </LinkButton>
        ) : (
          <>
            <LinkButton onClick={resetLayout}>
              <span className="mr-1 text-xs">{t('grid.reset')}</span>
              <ArrowPathRoundedSquareIcon className="size-4" />
            </LinkButton>
            <LinkButton onClick={discardUiLayoutChanges}>
              <span className="mr-1 text-xs">{t('grid.discard')}</span>
              <TrashIcon className="size-4" />
            </LinkButton>
            <LinkButton onClick={storeUiLayoutChanges}>
              <span className="mr-1 text-xs">{t('grid.save')}</span>
              <LockOpenIcon className="size-4 animate-bounce" />
            </LinkButton>
          </>
        )}
      </div>
      <div className="relative w-full">
        <ResponsiveGridLayout
          className="layout"
          layouts={editableLayouts}
          breakpoints={BREAKPOINTS}
          cols={COLS}
          rowHeight={34}
          width={1200}
          isDraggable={!uiLocked}
          isResizable={!uiLocked}
          onLayoutChange={handleLayoutChange}
          containerPadding={[0, 0]}
          preventCollision={false}
          isBounded={true}
          resizeHandles={!uiLocked ? ['se'] : []}
        >
          {Object.entries(gridComponents).map(([key, component]) => (
            <div key={key} className="group relative">
              <FloatingLayout uiLocked={uiLocked}>{component}</FloatingLayout>
            </div>
          ))}
        </ResponsiveGridLayout>
      </div>
    </div>
  )
}

export default TokenGrid

const FloatingLayout = memo(function FloatingLayout({
  uiLocked,
  children,
  className,
}: {
  uiLocked: boolean
  children: ReactNode
  className?: string
}) {
  const { t } = useTranslation('token-page')
  return (
    <div
      className={`thin-scroll relative h-full overflow-auto overflow-x-hidden rounded-xl bg-th-bkg-1 ${className}`}
    >
      {!uiLocked ? (
        <div className="absolute left-0 top-0 z-50 size-full cursor-move opacity-80">
          <div className="relative z-50 flex h-full flex-col items-center justify-center text-th-fgd-1">
            <ArrowsPointingOutIcon className="size-6" />
            <div className="mt-2">{t('grid.reposition')}</div>
          </div>
          <div className="absolute left-0 top-0 size-full rounded-lg bg-th-bkg-2" />
        </div>
      ) : null}
      {children}
    </div>
  )
})
